import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {SplitPayment} from '@gatewee/types/payment';

@Component({
  selector: 'gatewee-split-payment',
  templateUrl: './gatewee-split-payment.component.html',
  styleUrls: ['./gatewee-split-payment.component.css'],
  standalone: false,
})
export class GateweeSplitPaymentComponent implements OnInit {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formArray!: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formSaved: any[] = [];
  saved: boolean[] = [];
  emptySplit = true;

  @Input() whiteListSplit: {label: string; value: string | number}[] = [];
  @Input() recipientId: number | string | null = '';

  @Output() saveItem = new EventEmitter<{index: number; value: SplitPayment}>();
  @Output() splitsChange = new EventEmitter<SplitPayment[]>();
  @Output() splitRemoved = new EventEmitter<number>();

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.formArray = this.fb.array([]);
  }

  createSplitForm(): FormGroup {
    const group = this.fb.group({
      recipientId: [null, [Validators.required]],
      amount: [null, [Validators.required]],
      type: ['fixed', [Validators.required]],
    });

    group.get('type')?.valueChanges.subscribe(type => {
      const amountCtrl = group.get('amount');

      if (type === 'percentage') {
        amountCtrl?.setValidators([
          Validators.required,
          this.maxPercentValidator(100),
        ]);
      } else {
        amountCtrl?.setValidators([Validators.required]);
      }

      amountCtrl?.updateValueAndValidity();
    });

    return group;
  }

  setSplitType(i: number, type: 'fixed' | 'percentage'): void {
    const group = this.formArray.at(i) as FormGroup;
    group.get('type')?.setValue(type);
  }

  edit(i: number): void {
    this.saved[i] = false;
    const saved = this.formSaved[i] as SplitPayment;
    if (!saved) return;

    this.formArray.at(i).patchValue({
      recipientId: this.recipientIdForSelect(saved.recipientId),
      amount: saved.amount,
      type: saved.type,
    });
  }

  /** Match whitelist item value type (string) so gatewee-select-v3 can resolve the option. */
  private recipientIdForSelect(
    recipientId: string | number,
  ): string | number | null {
    const match = this.whiteListSplit.find(
      item => Number(item.value) === Number(recipientId),
    );
    return match?.value ?? recipientId;
  }

  addSplitForm(): void {
    this.formArray.push(this.createSplitForm());

    this.formSaved.push(null);
    this.saved.push(false);

    this.emptySplit = false;
  }

  emitSplits() {
    this.splitsChange.emit(
      this.formSaved.filter(item => item !== null) as SplitPayment[],
    );
  }

  removeSplit(index: number): void {
    this.formArray.removeAt(index);

    this.formSaved.splice(index, 1);
    this.saved.splice(index, 1);

    if (this.formArray?.length === 0) {
      this.emptySplit = true;
    }

    this.splitRemoved.emit(index);
    this.emitSplits();
  }

  saveSingle(i: number): void {
    const form = this.formArray.at(i) as FormGroup;

    if (form.valid) {
      const rawValue = form.getRawValue();

      const value: SplitPayment = {
        recipientId: Number(rawValue.recipientId),
        amount: Number(rawValue.amount),
        type: (form.get('type')?.value ?? 'fixed') as 'fixed' | 'percentage',
      };

      this.formSaved[i] = value;
      this.saved[i] = true;

      this.saveItem.emit({index: i, value});
      this.emitSplits();
    }
  }

  maxPercentValidator(max: number) {
    return (control: AbstractControl) => {
      const value = Number(control.value);

      if (isNaN(value)) return null;

      return value > max ? {notMoreThan100: true} : null;
    };
  }

  showValue(formValue: SplitPayment) {
    if (formValue.type === 'percentage') {
      return formValue.amount + '%';
    } else {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(formValue.amount);
    }
  }

  showRecipient(formValue: SplitPayment) {
    return (
      this.whiteListSplit.find(
        item => Number(item.value) === formValue.recipientId,
      )?.label || formValue.recipientId
    );
  }
}
